// PCATabControls.tsx (icons + tooltips version)
import React, { useCallback, useMemo } from 'react';
import { Box, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import type { Campaign } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { Translate, useCommonTranslation } from 'c-translation';
import {
    ExpandLess,
    ExpandMore,
    ReportProblemOutlined,
    ErrorOutline,
    Layers,
} from '@mui/icons-material';
import PCASettingsDialog from './PCASettingsDialog';
import { hidePcaTabControls, pcaTabStatus } from './pcaTabStatus';

type Props = {
    id: number;
    showTypeToggle?: boolean;
    isOpen?: boolean;
    onToggle?: () => void;
    showSettings?: boolean;
    bigTitle?: boolean;
    campaign?: Campaign;
    title?: React.ReactNode;
    alwaysShowTitle?: boolean;
    showStatusIcons?: boolean;
};

const PCATabControls: React.FC<Props> = ({
    id,
    showTypeToggle = false,
    isOpen = false,
    onToggle,
    showSettings = true,
    bigTitle = false,
    campaign,
    title,
    alwaysShowTitle = false,
    showStatusIcons = true,
}) => {
    const t = useCommonTranslation();

    const stopAll = useCallback((e: React.SyntheticEvent) => {
        e.stopPropagation();
        e.preventDefault?.();
    }, []);

    const { hasAwaitingData, hasInspectingData, needsBookingValidation } = useMemo(
        () => pcaTabStatus(campaign),
        [campaign],
    );

    // If absolutely nothing to show, bail out (keeps your previous behavior)
    if (hidePcaTabControls(campaign, alwaysShowTitle)) {
        return null;
    }

    return (
        <Box display="flex" alignItems="center" gap={1}>
            {bigTitle ? (
                <Typography component="span" variant="h1">
                    {title ?? t('Modules.Main.Campaigns.Overview.Tabs.reporting')}
                </Typography>
            ) : (
                <Typography component="span">
                    {title ?? t('Modules.Main.Campaigns.Overview.Tabs.reporting')}
                </Typography>
            )}

            {showTypeToggle && (
                <IconButton
                    size="small"
                    aria-label="Toggle reporting submenu"
                    aria-expanded={isOpen ? 'true' : undefined}
                    onMouseDown={stopAll}
                    onClick={e => {
                        stopAll(e);
                        onToggle?.();
                    }}
                >
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
            )}

            {showSettings && (
                <Box onClick={stopAll}>
                    <IfHasAllPermissions permissions={[PermissionName.AdminCampaignPca_settings]}>
                        <PCASettingsDialog id={id} />
                    </IfHasAllPermissions>
                </Box>
            )}

            {showStatusIcons && (
                <Box
                    onMouseDown={stopAll}
                    onClick={stopAll}
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                >
                    <IfHasAllPermissions permissions={[PermissionName.GeneralView_data_status]}>
                        {hasAwaitingData && (
                            <Tooltip
                                arrow
                                title={
                                    <span>
                                        <Translate path="Modules.Main.Campaigns.DataStatus.awaitingDataLabelAlert" />
                                    </span>
                                }
                            >
                                <IconButton size="small" sx={{ color: 'warning.main', p: 0.5 }}>
                                    <ReportProblemOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        {hasInspectingData && (
                            <Tooltip
                                arrow
                                title={
                                    <span>
                                        <Translate path="Modules.Main.Campaigns.DataStatus.inspectingDataLabelAlert" />
                                    </span>
                                }
                            >
                                <IconButton size="small" sx={{ color: 'warning.main', p: 0.5 }}>
                                    <ErrorOutline fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </IfHasAllPermissions>

                    <IfHasAllPermissions
                        permissions={[PermissionName.GeneralView_data_status_booking]}
                    >
                        {needsBookingValidation && (
                            <Tooltip
                                arrow
                                slotProps={{ tooltip: { sx: { p: 1 } } }}
                                title={
                                    <Box display="flex" flexDirection="column">
                                        <span>
                                            <Translate path="Modules.Main.Campaigns.DataStatus.bookingPlanValidatedLabelAlert" />
                                        </span>
                                        <Link
                                            href="mailto:data@uniledsoftware.com"
                                            target="_blank"
                                            sx={{ mt: 0.5, color: 'white' }}
                                        >
                                            data@uniledsoftware.com
                                        </Link>
                                    </Box>
                                }
                            >
                                <IconButton size="small" sx={{ color: 'warning.main', p: 0.5 }}>
                                    <Layers fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </IfHasAllPermissions>
                </Box>
            )}
        </Box>
    );
};

export default PCATabControls;
